import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { THorizontalBarChartCompanies } from './types.d';
import { upperCase } from '../../../../infrastructure/utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);
const barGradientPlugin = {
  id: 'barGradientPlugin',
  beforeDatasetsDraw: (chart: ChartJS<'bar'>) => {
    const { ctx, chartArea, data } = chart;
    if (!chartArea) return;

    const gradients = [
      ['#A8D8EA', '#7EC8E3'],
      ['#FFD3B6', '#FFBE9F'],
      ['#D5AAFF', '#C490E4'],
      ['#FF9AA2', '#FF8090'],
      ['#B5EAD7', '#9ADBC4'],
      ['#C7CEEA', '#ACB5DB'],
      ['#FFDAC1', '#FFC9A8'],
    ];

    data.datasets[0].backgroundColor = data?.labels?.map((_: unknown, index: number) => {
      const [from, to] = gradients[index % gradients.length];
      const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

      gradient.addColorStop(0, from);
      gradient.addColorStop(1, to);

      return gradient;
    });
  },
};

const HorizontalBarChartCompanies: React.FC<THorizontalBarChartCompanies> = ({
  portfolioCompanies,
}) => {
  const theme = useTheme();
  const labels = portfolioCompanies?.map((x) => upperCase(x?.EMPRESA));
  const quantities = portfolioCompanies?.map((x) => x.CANTIDAD);
  const percentages = portfolioCompanies?.map((x) => x.PORCENTAJE);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Créditos otorgados',
          data: quantities,
          borderRadius: 16,
          maxBarThickness: 56,
          borderWidth: 1.5,
          borderColor: 'rgba(0,0,0,0.08)',
        },
      ],
    }),
    [portfolioCompanies]
  );

  const options: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    animation: {
      duration: 900,
      easing: 'easeOutCubic',
      delay(ctx) {
        return ctx.dataIndex * 60;
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#1e3a5f',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        padding: 12,
        callbacks: {
          title: (ctx) => ctx[0].label,
          label: (ctx) => {
            const index = ctx.dataIndex;
            return [` Cantidad: ${quantities[index]}`, ` Participación: ${percentages[index]}%`];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: 'rgba(148,163,184,0.35)',
          lineWidth: 1.4,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.primary,
          font: {
            weight: 500,
          },
        },
      },
    },
  };

  return <Bar data={data} options={options} plugins={[barGradientPlugin]} />;
};

export default HorizontalBarChartCompanies;
